import { useState, useMemo, useEffect, useRef } from "react";
import {
	useMaterialReactTable,
	MaterialReactTable,
} from "material-react-table";
import { Box, Tooltip,IconButton } from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { MDBRow, MDBCol } from "mdbreact";

import {
	ICONS,
	Button,
	Icon,
	Input,
	Label,
	AbsDropDown,
} from "abs-ui-components";
import AbsModal from "../../../common/absModal/AbsModal";
import { addWcn, generateAccessToken, getWCNsDetails } from "../../../services/AdminService";
import { emitToasterMsg } from "../../../services/toasterUtility";
import { preventScientificNotation } from "../../../services/utilities";
import { RiskAssessment } from "../../RiskAssessment/RiskAssessment";
import ClearIcon from "@mui/icons-material/Clear";

const ManageWCNPage = () => {

	const formRef = useRef(null);
	const [wcnsInfoData, setWcnsInfoData] = useState([]);
	const [addOrEditWCN, setAddOrEditWCN] = useState(false);
	const [editWcn, setEditWCN] = useState(null);
	const [type, setType] = useState("Add");
	const [isLoading, setLoading] = useState(false);
	const [isReFetch, setReFetch] = useState(false);
	const [manageWCNSearchValue, setManageWCNSearchValue] = useState("");
	const [manageWCNColumnSearchValue, setManageWCNColumnSearchValue] =
		useState([]);
	const [selectAll, setSelectAll] = useState(false);
	const [wcnPagination, setWcnPagination] = useState({
		pageIndex: 0,
		pageSize: 25,
	});
	const [rowCount, setRowCount] = useState(0);
	const [disableButton, setButtonDisable] = useState(true)
	const [showTokenBtn, setShowTokenBtn] = useState(false)
	const [wcnSort, setWcnSort] = useState([]);
	const [previousGlobalFilter, setPreviousGlobalFilter] = useState("");
	const [searchInput, setSearchInput] = useState("");

	const yesOrNoOptions = [
		{
			label: "No",
			value: false,
		},
		{
			label: "Yes",
			value: true,
		},
	];
	const getBoxColorClass = (value) => {
		switch (value) {
			case "High":
			case "High Risk":
			case "Highest":
				return "high";
			case "Medium":
			case "Medium Risk":
				return "medium";
			case "Low":
			case "Low Risk":
			case "Very Low":
				return "low";
			default:
				return "unknown"; // Default color for unknown values
		}
	};
	
	const renderColoredBoxCell = (cell) => (
		<div className={`colored-box ${getBoxColorClass(cell.getValue())}`}>
			{cell.getValue()}
		</div>
	);

	const renderDetectionRiskCell = (cell) => (
	<div
		className={`detection-risk-box ${getBoxColorClass(cell.getValue())}`}>
			{cell.getValue()}
	</div>
	);

	const manageWCNColumns = useMemo(
		() => [
			{
				header: "WCN",
				enableHiding: false,
				accessorKey: "WcnNumber",
			},
			{
				header: "Company Name",
				accessorKey: "CompanyName",
			},
			// {//Future scope
			// 	header: "Combined Risk",
			// 	accessorKey: "CombinedRiskId",
			// 	accessorFn: (row) => {
			// 		if (row.CombinedRiskId) {
			// 			if (row.CombinedRiskId == "1") {
			// 				return "Low";
			// 			}
			// 			if (row.CombinedRiskId == "2") {
			// 				return "Medium";
			// 			}
			// 			if (row.CombinedRiskId == "3") {
			// 				return "High";
			// 			}
			// 		}
			// 	},
			// },
			{
				header: "Api Access Token",
				accessorKey: "ApiAccessToken",
				enableSorting: false,
			},
			{
				header: "DCS",
				accessorKey: "IsDcs",
				enableSorting: false,
			},
			{
				header: "EUMRV",
				accessorKey: "IsEumrv",
				enableSorting: false,
			},
			{
				header: "UKMRV",
				accessorKey: "IsUkmrv",
				enableSorting: false,
			},
			{
				header: "Inherent Risk",
				accessorKey: "InherentRisk",
				enableSorting: false,
				Cell: ({ cell }) => renderColoredBoxCell(cell),
			},
			{
				header: "Control Risk",
				accessorKey: "ControlRisk",
				enableSorting: false,
				Cell: ({ cell }) => renderColoredBoxCell(cell),
			},
			{
				header: "Detection Risk",
				accessorKey: "DetectionRisk",
				enableSorting: false,
				Cell: ({ cell }) => renderDetectionRiskCell(cell),
			},
		],
		[]
	);

	let methods = useForm();
	const {
		control,
		handleSubmit,
		formState: { errors },
		reset,
	} = methods;

	const handleWcnFormSubmit = async (data) => {
		//for create & update wcn
		let requestPayload = {};

		if (type === "Edit") {
			requestPayload = {
				wcnMstId: editWcn?.wcnMstId,
				wcnNumber: data?.wcnNumber,
				companyName: data?.companyName,
				combinedRiskId: 1,
				isDcs: data.isDcs?.value,
				isEumrv: data?.isEumrv?.value,
				isUkmrv: data?.isUkmrv?.value,
				controlRiskId: data?.controlRisk.value,	
				inherentRiskId:  data?.inherentRisk.value,
			};
		} else {
			requestPayload = {
				wcnMstId: 0,
				wcnNumber: data?.wcnNumber,
				companyName: data?.companyName,
				combinedRiskId: 1,
				isDcs: data.isDcs?.value,
				isEumrv: data?.isEumrv?.value,
				isUkmrv: data?.isUkmrv?.value,
				controlRiskId: data?.controlRisk.value,	
				inherentRiskId:  data?.inherentRisk.value,
			};
		}
		try {
			const response = await addWcn(requestPayload, type === "Edit");
			if (response && response?.isSuccess) {
				setReFetch(true);
				emitToasterMsg(
					"success",
					"WCN",
					`${type === "Edit" ? "Updated" : "Added"} Successfully`
				);
				setButtonDisable(true)
			} else if (response && response?.isSuccess === false) {
				emitToasterMsg("error", "WCN", response?.errorMessage);
			} else {
				console.error("Error:", response?.errorMessage);
			}
			reset();
		} catch (error) {
			reset();
		}
	};

	const ManageWcnInfo = (patchWcnValues) => {
		const editWcnValues = patchWcnValues.original;
		setShowTokenBtn(!editWcnValues.ApiAccessToken);
		const patchWcn = {
			wcnMstId: editWcnValues?.WcnMstId,
			wcnNumber: editWcnValues?.WcnNumber,
			companyName: editWcnValues?.CompanyName,
			combinedRiskId:
				editWcnValues?.CombinedRiskId === "High"
					? 1
					: editWcnValues?.CombinedRiskId === "Medium"
					? 2
					: 3,
			isDcs: editWcnValues?.IsDcs === "Yes" ? true : false,
			isEumrv: editWcnValues?.IsEumrv === "Yes" ? true : false,
			isUkmrv: editWcnValues?.IsUkmrv === "Yes" ? true : false,
			SOF: editWcnValues?.SOF || false,
			inherentRiskId: editWcnValues?.InherentRiskId,
			controlRiskId: editWcnValues?.ControlRiskId,
			detectionRiskId: editWcnValues?.DetectionRiskId,
		};
		reset();
		setEditWCN(patchWcn);
		setButtonDisable(false)
		setType("Edit");
		setAddOrEditWCN(!addOrEditWCN);
	};


	const generateToken = async (wcnId) => {
		try {
			const response = await generateAccessToken(wcnId)
			if (response && response.isSuccess) {
				setReFetch(true);
				emitToasterMsg("success", "WCN", "Access token generated successfully");
				setAddOrEditWCN(!addOrEditWCN)
			} else {
				emitToasterMsg("error", "WCN", response?.errorMessage);
			}
		}
		catch (error) {
			console.error(error)

		}
	}

	const toggleOpen = () => {
		setShowTokenBtn(false)
		setButtonDisable(true)
		reset();
		setAddOrEditWCN(!addOrEditWCN);
		setEditWCN(null);
		setType("Add");
	};

	const handleSelectAll = (event) => {
		setSelectAll(event.target.checked);
	};

	const wcnDetailsTable = useMaterialReactTable({
		columns: manageWCNColumns,
		data: wcnsInfoData,
		enableRowSelection: false,
		manualSorting: true,
		enableStickyHeader: true,
		manualPagination: true,
		manualFiltering: true,
		enableRowActions: true,
		enableColumnActions: false,
		enableColumnFilters: false,
		enableFullScreenToggle: false,
		enableDensityToggle: false,
		rowCount,
		getRowId: (row) => row.WcnMstId,
		onRowSelectionChange: setEditWCN,
		positionActionsColumn: "last",
		onColumnFiltersChange: setManageWCNColumnSearchValue,
		onPaginationChange: setWcnPagination,
		onSortingChange: setWcnSort,
		state: {
			globalFilter: searchInput,
			columnFilters: manageWCNColumnSearchValue,
			pagination: wcnPagination,
			isLoading: isLoading,
			sorting: wcnSort,
		},
		initialState: {
			density: "compact",
			columnOrder: ["WCN", "Company Name", "DCS", "EUMRV", "UKMRV", "Inherent Risk", "Control Risk", "Detection Risk"],
		},
		muiPaginationProps: {
			rowsPerPageOptions: [25, 50, 75, 100],
			showFirstButton: true,
			showLastButton: true,
		},
		renderRowActions: ({ row }) => (
			<Box sx={{ display: "flex", gap: "1rem", width: "120px" }}>
				<Tooltip title="Manage">
					<Button onClick={() => ManageWcnInfo(row)}>Edit</Button>
				</Tooltip>
			</Box>
		),
		muiTableContainerProps: {
			sx: {
				maxHeight: "calc(100vh - 376px)",
			},
		},
		muiTableHeadCellProps: ({ column }) => ({
			id: `${column.columnDef.accessorKey}`,
			sx: {
				color: "rgb(255 255 255)",

				"& .MuiButtonBase-root": {
					color: "white !important",
					"& .MuiTableSortLabel-icon": {
						color: "white !important",
					},
				},
			},
		}),
		muiFilterTextFieldProps: {
			sx: {
				"& .MuiInput-root": {
					color: "white !important",
				},
				"& .MuiButtonBase-root": {
					color: "white !important",
				},
				"& .MuiInputBase-root::after": {
					transform: "none",
					borderBottom: "2px solid white",
				},
				"& .MuiSvgIcon-root": {
					color: "white",
				},
			},
		},
		muiColumnActionsButtonProps: {
			sx: {
				color: "white",
			},
		},
		muiSelectAllCheckboxProps: {
			title: selectAll ? "Toggle deselect all" : "Toggle select all",
			onClick: (e) => handleSelectAll(e),
			sx: {
				color: "white",
			},
		},
		muiTableHeadRowProps: {
			sx: {
				backgroundColor: "#042a4c !important",

				"& .MuiTableCell-head::before": {
					backgroundColor: "#042a4c !important",
				},
			},
		},
		muiTablePaperProps: {
			sx: {
				boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
			},
		},
		muiSearchTextFieldProps: {
			placeholder: `Type and Enter`,
			sx: { minWidth: "400px" },
			value: searchInput, // controlled by local state
			onChange: (e) => {
				const rawValue = e.target.value;
				setSearchInput(rawValue); // only updates local input, no API call
			},
			onKeyDown: (e) => {
				if (e.key === "Enter") {
					const trimmed = searchInput.trim(); // trim spaces – you can add other transformations if needed
					setManageWCNSearchValue(trimmed);
					setWcnPagination((prev) => ({ ...prev, pageIndex: 0 }));
				}
			},
			InputProps: {
				endAdornment: searchInput && (
					<IconButton
						size="small"
						onClick={() => {
							setSearchInput("");
							setManageWCNSearchValue("");
							setManageWCNColumnSearchValue([]);
							setWcnPagination((prev) => ({ ...prev, pageIndex: 0 }));
						}}
						sx={{ visibility: searchInput ? "visible" : "hidden" }}
					>
						<ClearIcon fontSize="small" />
					</IconButton>
				),
			},
		},
	});

	const fetchWcnDetails = async (
		searchStr = "",
		pageIndex = 0,
		pageSize = 25,
		columnSearch,
		sortOptions = {
			sortField: "",
			isAscending: true,
		}
	) => {
		let requestPayload = {
			pagination: {
				page: pageIndex + 1,
				pageSize: pageSize,
			},
			globalFilter: "",
			filters: [],
			sortOption: {
				sortField: sortOptions.id,
				isAscending: !sortOptions.desc,
			},
		};

		if (searchStr !== "") {
			requestPayload = { ...requestPayload, globalFilter: searchStr };
		}

		setLoading(true);
		try {
			const response = await getWCNsDetails(requestPayload);
			if (response && response?.Data) {
				transformWCNData(response.Data);
				setRowCount(response?.Pagination?.TotalItems);
				setLoading(false);
			}
		} catch (error) {
			setLoading(false);
			console.error(error);
		}
	};
	const handleInputChange = () => {
		setButtonDisable(false);
	};

	const transformWCNData = (data) => {
		let WCNData = [];
		data.forEach((element) => {
			element.IsDcs = element.IsDcs === false ? "No" : "Yes";
			element.IsEumrv = element.IsEumrv === false ? "No" : "Yes";
			element.IsUkmrv = element.IsUkmrv === false ? "No" : "Yes";
			element.InherentRisk = element.InherentRisk || "Medium";
			element.ControlRisk = element.ControlRisk || "Medium";
			element.DetectionRisk = element.DetectionRisk || "Medium";
			WCNData.push(element);
		});
		setWcnsInfoData(WCNData);
	};
	useEffect(() => {
		if (
			manageWCNSearchValue !== "" &&
			previousGlobalFilter !== manageWCNSearchValue
		) {
			setPreviousGlobalFilter(manageWCNSearchValue);
			setWcnPagination({ ...wcnPagination, pageIndex: 0 });
			fetchWcnDetails(
				manageWCNSearchValue,
				0,
				wcnPagination.pageSize,
				manageWCNColumnSearchValue,
				wcnSort.length > 0 ? wcnSort[0] : wcnSort
			);
		} else {
			fetchWcnDetails(
				manageWCNSearchValue,
				wcnPagination.pageIndex,
				wcnPagination.pageSize,
				manageWCNColumnSearchValue,
				wcnSort.length > 0 ? wcnSort[0] : wcnSort
			);
		}
	}, [
		manageWCNSearchValue,
		manageWCNColumnSearchValue,
		wcnPagination.pageIndex,
		wcnPagination.pageSize,
		wcnSort,
	]);

	useEffect(() => {
		if (!addOrEditWCN);
	}, [addOrEditWCN]);

	useEffect(() => {
		if (isReFetch) {
			setAddOrEditWCN(false);
			fetchWcnDetails();
			setReFetch(false);
		}
	}, [isReFetch]);

	return (
		<div>
			<div className="d-flex justify-content-between">
				<h4>WCN Details</h4>
				<Button
					startIcon={<Icon icon={ICONS.ADD} />}
					onClick={toggleOpen}
				>
					{" "}
					Add WCN
				</Button>
			</div>
			<div className="mt-2">
				<MaterialReactTable table={wcnDetailsTable} />
			</div>
			{addOrEditWCN && (
				<AbsModal
					isOpen={addOrEditWCN}
					title={`${type} WCN`}
					onClose={setAddOrEditWCN}
					onRequestClose={setAddOrEditWCN}
					close={toggleOpen}
				>
					<FormProvider {...methods}> 
						<form
							onSubmit={handleSubmit(handleWcnFormSubmit)}
							className="add-wcn-form"
							id="add-wcn-form-id"
							ref={formRef}
						>
							<MDBRow>
								<MDBCol md="4" className="input-div-height">
									<Controller
										name="wcnNumber"
										control={control}
										rules={{
											...(!editWcn && {
												required: "WCN is required",
												pattern: {
													value: /^\d{6}$/,
													message:
														"WCN number must be exactly 6 characters",
												},
											}),
										}}
										defaultValue={
											editWcn?.wcnNumber?.trim() || ""
										}
										render={({ field }) => (
											<Input
												id="wcn"
												{...field}
												required={!editWcn}
												disabled={!!editWcn}
												onChange={(e) => {
													field.onChange(e);
													handleInputChange(e)
												}}
												className="inputHeight"
												minLength={6}
												maxLength={6}
												type="number"
												label="WCN"
												value={field.value}
												autoFocus={false}
												onKeyDown={(e) => {
													preventScientificNotation(e);
													// Prevent up and down arrow keys
													if (
														e.key === "ArrowUp" ||
														e.key === "ArrowDown"
													) {
														e.preventDefault();
													}
												}}
												onWheel={(e) => e.target.blur()}
											/>
										)}
									/>
									<div className="mt-1">
										{errors?.wcnNumber && (
											<div className="error">
												{errors?.wcnNumber?.message}
											</div>
										)}
									</div>
								</MDBCol>
								<MDBCol md="4" className="input-div-height">
									<Controller
										name="companyName"
										control={control}
										rules={{
											required: "Company Name is required",
										}}
										defaultValue={
											editWcn?.companyName?.trim() || ""
										}
										render={({ field }) => (
											<Input
												id="manage-wcn-companyName"
												{...field}
												required
												onChange={(e) => {
													field.onChange(e);
													handleInputChange(e);
												}}
												className="inputHeight"
												type="text"
												label="Company Name"
												value={field.value}
												autoFocus={false}
												title={field.value}
											/>
										)}
									/>
									<div className="mt-1">
										{errors?.companyName && (
											<div className="error">
												{errors?.companyName?.message}
											</div>
										)}
									</div>
								</MDBCol>

								{/*//Future scope <MDBCol md="4" className="mt-1 input-div-height">
									<Label
										bold
										name="Combined Risk"
										className="mb-1"
									/>
									<MDBRow className="d-flex">
										<MDBCol md="12">
											<Controller
												name="combinedRiskId"
												className="add-wcn-form-dropdown"
												control={control}
												rules={{}}
												defaultValue={
													editWcn?.combinedRiskId
														? {
																value: editWcn?.combinedRiskId,
																label: editWcn?.combinedRiskId,
														}
														: combinedRiskOptions[0]
												}
												render={({ field }) => (
													<div id="manage-wcn-combinedRisk">
														<AbsDropDown
															options={
																combinedRiskOptions
															}
															selected={
																field.value?.value
															}
															handleChange={(e) => {
																field.onChange(e);
															}}
														/>
													</div>
												)}
											/>
											{errors.combinedRiskId && (
												<div className="error-msg">
													{errors.combinedRiskId?.message}
												</div>
											)}
										</MDBCol>
									</MDBRow>
								</MDBCol> */}
							</MDBRow>
							<MDBRow>
								<MDBCol md="4" className="mt-3 input-div-height">
									<Label bold name="DCS" className="mb-1" />
									<MDBRow className="d-flex">
										<MDBCol md="12">
											<Controller
												name="isDcs"
												control={control}
												rules={{}}
												defaultValue={
													editWcn?.isDcs
														? {
																value: editWcn?.isDcs,
																label: editWcn.isDcs,
														}
														: yesOrNoOptions[0]
												}
												render={({ field }) => (
													<div id="manage-wcn-dcs">
														<AbsDropDown
															options={yesOrNoOptions}
															selected={
																field.value?.value
															}
															handleChange={(e) => {
																field.onChange(e);
																handleInputChange(e)
															}}
														/>
													</div>
												)}
											/>
											{errors.isDcs && (
												<div className="error-msg">
													{errors.isDcs?.message}
												</div>
											)}
										</MDBCol>
									</MDBRow>
								</MDBCol>
								<MDBCol md="4" className="mt-3 input-div-height">
									<Label bold name="EUMRV" className="mb-1" />
									<MDBRow className="d-flex">
										<MDBCol md="12">
											<Controller
												name="isEumrv"
												control={control}
												rules={{}}
												defaultValue={
													editWcn?.isEumrv
														? {
																value: editWcn?.isEumrv,
																label: editWcn?.isEumrv,
														}
														: yesOrNoOptions[0]
												}
												render={({ field }) => (
													<div id="manage-wcn-eumrv">
														<AbsDropDown
															options={yesOrNoOptions}
															selected={
																field.value?.value
															}
															handleChange={(e) => {
																field.onChange(e);
																handleInputChange(
																	e
																);
															}}
														/>
													</div>
												)}
											/>
											{errors.isEumrv && (
												<div className="error-msg">
													{
														errors.addwcnForm.isEumrv
															?.message
													}
												</div>
											)}
										</MDBCol>
									</MDBRow>
								</MDBCol>
								<MDBCol md="4" className="mt-3 input-div-height">
									<Label bold name="UKMRV" className="mb-1" />
									<MDBRow className="d-flex">
										<MDBCol md="12">
											<Controller
												name="isUkmrv"
												control={control}
												rules={{}}
												defaultValue={
													editWcn?.isUkmrv
														? {
																value: editWcn?.isUkmrv,
																label: editWcn?.isUkmrv,
														}
														: yesOrNoOptions[0]
												}
												render={({ field }) => (
													<div id="manage-wcn-ukmrv">
														<AbsDropDown
															options={yesOrNoOptions}
															selected={
																field.value?.value
															}
															handleChange={(e) => {
																field.onChange(e);
																handleInputChange(
																	e
																);
															}}
														/>
													</div>
												)}
											/>
											{errors.isUkmrv && (
												<div className="error-msg">
													{
														errors.addwcnForm.isUkmrv
															?.message
													}
												</div>
											)}
										</MDBCol>
									</MDBRow>
								</MDBCol>
							</MDBRow>

							<MDBRow>
								<RiskAssessment inherentRiskValue={editWcn?.inherentRiskId} controlRiskValue={editWcn?.controlRiskId} />
							</MDBRow>

							{/* 
							Future scope
							<MDBRow>
								<MDBCol md="4" className="mt-3 input-div-height">
									<Label bold name="SOF" className="mb-1" />
									<MDBRow className="d-flex">
										<MDBCol md="12">
											<Controller
												name="SOF"
												control={control}
												rules={{}}
												defaultValue={
													editWcn?.SOF
														? {
																value: editWcn?.SOF,
																label: editWcn?.SOF,
														}
														: yesOrNoOptions[0]
												}
												render={({ field }) => (
													<div id="manage-wcn-sof">
														<AbsDropDown
															options={yesOrNoOptions}
															selected={
																field.value?.value
															}
															handleChange={(e) => {
																field.onChange(e);
															}}
														/>
													</div>
												)}
											/>
											{errors.SOF && (
												<div className="error-msg">
													{errors.SOF?.message}
												</div>
											)}
										</MDBCol>
									</MDBRow>
								</MDBCol>
							</MDBRow> */}

							<div className="d-flex justify-content-end mt-5 mr-3">
								{showTokenBtn && (
									<>
										<Button
											onClick={() => {
												generateToken(editWcn?.wcnMstId);
											}}
											className="mr-3 token-btn text-center"
											variant="secondary"
										>
											Generate Access Token
										</Button>
									</>
								)}

								<button
									type="submit"
									className="mr-3 save-button"
									id="save-update-btn-portcode"
									disabled={disableButton}
								>
									{type === "Add" ? "Save" : "Update"}
								</button>
								<button
									id="cancel-btn-portcode"
									type="button"
									className="clear-button"
									onClick={() => {
										toggleOpen();
										reset();
									}}
								>
									Cancel
								</button>
							</div>
						</form>
					</FormProvider>
				</AbsModal>
			)}
		</div>
	);
};

export default ManageWCNPage;